import { useContext, useEffect, useRef, useState } from 'react'

import { reaction } from 'mobx'
import { MobXProviderContext } from 'mobx-react'

import { ISitePermitFilterInput } from '~/client/graph'
import {
  useGetSitePermitAggregationListQuery,
  useListenToSitePermitSubscription,
} from '~/client/graph/operations/generated/SitePermits.generated'
import Workflow, { IWorkflowDto } from '~/client/src/shared/models/Workflow'
import InitialState from '~/client/src/shared/stores/InitialState'

import { areIntervalTimesIntersects } from '../stores/ui/ProjectDate.store'

const defaultPagination: IPagination = {
  offset: 0,
  limit: 1_000_000,
}

interface IPagination {
  offset: number
  limit: number
}

/** Applies one permit, as the subscription or a save returned it, to the list. */
function mergeWorkflow(
  prev: Workflow[],
  id: string,
  dto: IWorkflowDto | null | undefined,
  filters: ISitePermitFilterInput,
): Workflow[] {
  if (!dto || dto.isDeleted) return prev.filter(item => item.id !== id)

  if (
    filters &&
    !areIntervalTimesIntersects(
      {
        startDate: new Date(dto.startDate),
        endDate: new Date(dto.endDate),
      },
      {
        startDate: new Date(filters.startDate),
        endDate: new Date(filters.endDate),
      },
    )
  )
    return prev

  if (prev.some(item => item.id === id))
    return prev.map(item => (item.id === id ? Workflow.fromDto(dto) : item))

  return [...prev, Workflow.fromDto(dto)]
}

// Danger: only use this hook if you need to fetch all workflows for a project (IE: in logistics)
export default function useWorkflows(
  projectId: string,
  currentUserId: string,
  filters: ISitePermitFilterInput,
  pagination: IPagination = defaultPagination,
  skip?: boolean,
) {
  const { state } = useContext(MobXProviderContext) as { state?: InitialState }
  const [workflows, setWorkflows] = useState<Workflow[]>([])

  const filtersRef = useRef(filters)
  filtersRef.current = filters

  const variables = {
    filter: {
      projectId,
      ...filters,
    },
    limit: pagination.limit,
    offset: pagination.offset,
  }
  const isSkipped = !projectId || skip

  // The list is the query's result plus every change since. Changes that land while the query is
  // still in flight would be wiped when its result replaces the list, so they are kept and applied
  // again on top of it. Recording starts afresh with each new request and stops once its result is
  // in; re-applying a change the result already holds is harmless, the merge is by id.
  const pendingChangesRef = useRef<
    { id: string; dto: IWorkflowDto | null | undefined }[]
  >([])
  const isQueryInFlightRef = useRef(false)
  const requestKey = isSkipped ? null : JSON.stringify(variables)
  const requestKeyRef = useRef<string | null>(null)
  if (requestKeyRef.current !== requestKey) {
    requestKeyRef.current = requestKey
    isQueryInFlightRef.current = requestKey !== null
    pendingChangesRef.current = []
  }

  const applyChange = (id: string, dto: IWorkflowDto | null | undefined) => {
    if (isQueryInFlightRef.current) pendingChangesRef.current.push({ id, dto })
    setWorkflows(prev => mergeWorkflow(prev, id, dto, filtersRef.current))
  }

  const { data: workflowsData, loading: workflowsLoading } =
    useGetSitePermitAggregationListQuery({
      variables,
      skip: isSkipped,
      fetchPolicy: 'no-cache',
    })

  useEffect(() => {
    const fromQuery = (workflowsData?.sitePermitsAggregation?.data || []).map(
      workflow => Workflow.fromDto(workflow),
    )

    if (!workflowsData) {
      setWorkflows(fromQuery)
      return
    }

    const pending = pendingChangesRef.current
    pendingChangesRef.current = []
    isQueryInFlightRef.current = false

    setWorkflows(
      pending.reduce(
        (list, change) =>
          mergeWorkflow(list, change.id, change.dto, filtersRef.current),
        fromQuery,
      ),
    )
  }, [workflowsData])

  // A request that settles without data (an error) leaves nothing to re-apply onto.
  useEffect(() => {
    if (!workflowsLoading && !workflowsData) {
      isQueryInFlightRef.current = false
      pendingChangesRef.current = []
    }
  }, [workflowsLoading, workflowsData])

  useListenToSitePermitSubscription({
    variables: {
      projectId,
      currentUserId,
    },
    onSubscriptionData: data => {
      const { sitePermit } = data.subscriptionData.data
      if (!sitePermit) return

      applyChange(sitePermit.id, sitePermit.item)
    },
  })

  // A permit saved here before the subscription above is live would otherwise never reach the
  // list: it loads once, and from then on only the subscription adds to it. The save's own result
  // is merged by the same rule, so whichever of the two arrives first, the outcome is the same.
  const applyChangeRef = useRef(applyChange)
  applyChangeRef.current = applyChange

  useEffect(() => {
    if (!state) return undefined

    return reaction(
      () => state.lastSavedWorkflows,
      saved =>
        saved
          .filter(dto => dto?.projectId === projectId)
          .forEach(dto => applyChangeRef.current(dto.id, dto)),
    )
  }, [state, projectId])

  return {
    workflows,
    workflowsLoading,
  }
}
